import PropTypes from 'prop-types';
import { isValidAvatar, getAvatarTestId } from '../utils/avatar';
import './RoomAvatar.css';

/**
 * RoomAvatar component - Pure presentational component for displaying room avatars
 *
 * Displays avatars in one of three modes:
 * 1. Preview mode (previewUrl provided) - Shows a preview of pending changes
 * 2. Avatar mode (avatarUrls provided) - Shows current state (1-2 avatars or placeholder)
 * 3. Placeholder mode (no avatars) - Shows initial letter
 *
 * @param {Array<string>} avatarUrls - Array of 0-2 avatar URLs to display (current state)
 * @param {string} previewUrl - Optional preview URL for pending changes (overrides avatarUrls)
 * @param {string} placeholderText - Text to show in placeholder (default: '?')
 * @param {string} roomId - Room ID for generating deterministic test identifiers
 */
const RoomAvatar = ({
  avatarUrls = [],
  previewUrl = null,
  placeholderText = '?',
  roomId = null,
}) => {
  // Get initial letter for placeholder
  const placeholderInitial = placeholderText?.charAt(0)?.toUpperCase() || '?';

  // Generate test ID hash for automation testing
  const avatarHash = roomId ? getAvatarTestId(roomId) : 'unknown';

  // If previewUrl is provided, use it directly (for pending avatar changes)
  if (previewUrl !== null && isValidAvatar(previewUrl)) {
    return (
      <div className="room-avatar-container">
        <img
          className="room-avatar-single"
          src={previewUrl}
          alt="Room Avatar Preview"
          crossOrigin="anonymous"
          data-testid={`avatar-room-${avatarHash}-preview`}
          data-avatar-type="image"
          data-avatar-hash={avatarHash}
          data-avatar-state="preview"
          data-loaded="false"
          onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
        />
      </div>
    );
  }

  // Extract and filter valid avatar URLs
  const validAvatarUrls = avatarUrls
    .map(avatar => {
      // Handle both string URLs and objects with url property
      if (typeof avatar === 'string') {
        return avatar;
      }
      return avatar?.url;
    })
    .filter(url => url && isValidAvatar(url));

  // No avatars - show placeholder
  if (validAvatarUrls.length === 0) {
    return (
      <div className="room-avatar-container">
        <div
          className="room-avatar-placeholder"
          data-testid={`avatar-room-${avatarHash}`}
          data-avatar-type="initials"
          data-avatar-hash={avatarHash}
        >
          {placeholderInitial}
        </div>
      </div>
    );
  }

  // Single avatar case
  if (validAvatarUrls.length === 1) {
    return (
      <div className="room-avatar-container">
        <img
          className="room-avatar-single"
          src={validAvatarUrls[0]}
          alt="Room Avatar"
          crossOrigin="anonymous"
          data-testid={`avatar-room-${avatarHash}`}
          data-avatar-type="image"
          data-avatar-hash={avatarHash}
          data-loaded="false"
          onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
        />
      </div>
    );
  }

  // Dual avatar case (2 or more valid avatars)
  if (validAvatarUrls.length >= 2) {
    return (
      <div className="room-avatar-container">
        <img
          className="room-avatar-first"
          src={validAvatarUrls[0]}
          alt="Avatar 1"
          crossOrigin="anonymous"
          data-testid={`avatar-room-${avatarHash}-first`}
          data-avatar-type="image"
          data-avatar-hash={avatarHash}
          data-avatar-position="first"
          data-loaded="false"
          onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
        />
        <img
          className="room-avatar-second"
          src={validAvatarUrls[1]}
          alt="Avatar 2"
          crossOrigin="anonymous"
          data-testid={`avatar-room-${avatarHash}-second`}
          data-avatar-type="image"
          data-avatar-hash={avatarHash}
          data-avatar-position="second"
          data-loaded="false"
          onLoad={(e) => e.target.setAttribute('data-loaded', 'true')}
        />
      </div>
    );
  }

  // Fallback to placeholder (should not reach here due to earlier check)
  return (
    <div className="room-avatar-container">
      <div
        className="room-avatar-placeholder"
        data-testid={`avatar-room-${avatarHash}`}
        data-avatar-type="initials"
        data-avatar-hash={avatarHash}
      >
        {placeholderInitial}
      </div>
    </div>
  );
};

RoomAvatar.propTypes = {
  avatarUrls: PropTypes.arrayOf(
    PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.shape({
        url: PropTypes.string,
      }),
    ]),
  ),
  previewUrl: PropTypes.string,
  placeholderText: PropTypes.string,
  roomId: PropTypes.string,
};

export default RoomAvatar;
